/**
 * Service-worker registration for the Avida Care PWA.
 *
 * Skips registration entirely on localhost dev (CRA hot reload + SW
 * caching == confusing). On production hosts the SW boots silently
 * the first time the app loads while online.
 *
 * Exposes a 'prompt' helper so the FieldVisitFlow page can show its
 * own contextual "Install Avida Care on this tablet" CTA.
 */
let _installPrompt = null;
const installListeners = new Set();

function emitInstall(state) {
  installListeners.forEach((cb) => { try { cb(state); } catch {/* */} });
}

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  const host = window.location.hostname;
  const isLocal = ["localhost", "127.0.0.1", "0.0.0.0", "::1"].includes(host);
  // Allow SW everywhere except CRA dev — preview/prod hosts both work.
  if (isLocal) return;

  // Listen for install prompts
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _installPrompt = e;
    emitInstall({ available: true });
  });
  window.addEventListener("appinstalled", () => {
    _installPrompt = null;
    emitInstall({ installed: true });
  });

  // Register (handle the case where 'load' already fired)
  const doRegister = () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .then((reg) => {
        // eslint-disable-next-line no-console
        console.info("[avida-pwa] service worker registered:", reg.scope);
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              try { nw.postMessage({ type: "AVIDA_SKIP_WAITING" }); } catch {/* */}
            }
          });
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[avida-pwa] SW registration failed:", err);
      });
  };
  if (document.readyState === "complete") {
    doRegister();
  } else {
    window.addEventListener("load", doRegister, { once: true });
  }
}

export function onInstallAvailability(cb) {
  installListeners.add(cb);
  // Fire current state once
  cb({ available: !!_installPrompt });
  return () => installListeners.delete(cb);
}

export async function promptInstall() {
  if (!_installPrompt) return { outcome: "unavailable" };
  _installPrompt.prompt();
  const choice = await _installPrompt.userChoice;
  _installPrompt = null;
  emitInstall({ available: false });
  return choice;
}
